import DownloadJob from '@components/icons/DownloadJob.svg';
import EditIcon from '@components/icons/Edit.svg';
import JobsIcon from '@components/icons/Jobs.svg';
import Share from '@components/icons/ShareIcon.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import MoveIcon from '@components/icons/Move.svg';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { getLinkPrefix, handleDownloadError } from '@helpers/jobLinkHelpers';
import { downloadFromUrl } from '@helpers/downloadFromUrl';
import { jobCanBeResubmitted } from '@helpers/jobStatus/jobCanBeResubmitted';
import { jobCanBeShared } from '@helpers/jobStatus/jobCanBeShared';
import { jobIsEditable } from '@helpers/jobStatus/jobIsEditable';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { useMutation } from '@tanstack/react-query';
import { JobResponseDto, JobsService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { Td, Tr } from '@components/core-ui/Table';
import { FileIconLinkButton } from '@components/core-ui/form-elements/buttons/Button/variations/FileIconLinkButton';
import { jobCanBeDownloaded } from '@helpers/jobStatus/jobCanBeDownloaded';

type TProps = {
  job: JobResponseDto;
  reload: () => void;
  skipFirstRow?: boolean;
  filesRowColSpan?: number;
};

export const ButtonsTRow = ({ job, reload, filesRowColSpan = 6, skipFirstRow = false }: TProps) => {
  const { call } = useAPI();
  const { workspace, organization, me } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const navigate = useNavigate();
  const workspaceId = job.workspaceId ?? workspace?.id ?? '';
  const workspaceSlug = job.workspaceSlug ?? workspace?.slug ?? '';

  const isVsAdmin = me?.roles?.includes('ROLE_VS_ADMIN');
  const showDeleteButton = organization?.permissions?.deleteJobs;

  const handleCopyButtonClick = (link: string) => {
    navigator.clipboard.writeText(link);
  };

  const handleEditJob = (id: string) => {
    navigate(routes.jobEdit.make(organization?.slug ?? '', workspaceSlug || 'undefined', id));
  };
  const handleResubmitJob = () => {
    reload();
    closeModal();
  };

  const handleResubmitJobModalShow = (id: string) => {
    openModal(ModalNamesEnum.ResubmitJobModal, {
      onSuccess: handleResubmitJob,
      id,
    });
  };

  const downloadFile = async (jobId: string, fileName: string) => {
    await downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/transcript/${encodeURIComponent(fileName)}`,
      fileName,
    ).catch(handleDownloadError);
  };

  const removeJobMutation = useMutation({
    mutationFn: (jobId: string) => call(() => JobsService.softDeleteWsJob({ wsid: workspaceId, jobId })),
    onSuccess: () => {
      reload();
      closeModal();
    },
    onError: notifyApiCallError,
  });

  const showMoveJobToWSModal = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.MoveJobToWSModal, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      job,
    });
  };

  const handleRemoveJobModalShow = (id: string) => {
    openModal(ModalNamesEnum.DeleteJobModal, {
      onSuccess: () => removeJobMutation.mutateAsync(id).catch(() => undefined),
    });
  };

  const canSubmitJobs = !!organization?.permissions?.submitJobs;
  const showMoveToWSButton = !job.asOneLocked && canSubmitJobs;
  const showShareButton = jobCanBeShared(job.status);
  const showResubmitButton = isVsAdmin && jobCanBeResubmitted(job.status);
  const showEditButton = jobIsEditable(job.status);
  const showDownloadLinks =
    (me?.id === job?.assigneeId || organization?.permissions?.viewJobs === 'all') && jobCanBeDownloaded(job.status);

  return (
    <Tr className="pb-3 !border-t-0">
      {!skipFirstRow ? <Td className="pb-3"></Td> : null}
      <Td colSpan={filesRowColSpan} className="pb-3">
        {showDownloadLinks && job.results?.length ? (
          <div className="flex gap-4">
            {job.results?.map((fileName) => {
              const ext = (fileName || '').split('.').pop() || '';
              return (
                <FileIconLinkButton
                  data-testid={`jobDownloadItem${job.name}`}
                  key={fileName}
                  onClick={() => downloadFile(job.id, fileName)}
                  className="downloadLink cursor-pointer !gap-0"
                >
                  <DownloadJob /> {ext.toUpperCase()}
                </FileIconLinkButton>
              );
            })}
          </div>
        ) : null}
      </Td>
      <Td className="pb-3" colSpan={2}>
        <div className="flex gap-2 justify-end">
          {showMoveToWSButton ? (
            <IconButton title="Move to another Workspace" onClick={() => showMoveJobToWSModal(job)}>
              <MoveIcon />
            </IconButton>
          ) : null}

          {showShareButton ? (
            <IconButton
              title="Copy link to clipboard"
              onClick={() => {
                toast.success('The link has been copied to the clipboard', {
                  duration: 2000,
                });
                handleCopyButtonClick(`${getLinkPrefix()}${job.id}`);
              }}
            >
              <Share />
            </IconButton>
          ) : null}

          {showResubmitButton ? (
            <IconButton title="Resubmit" onClick={() => handleResubmitJobModalShow(job.id)}>
              <JobsIcon />
            </IconButton>
          ) : null}

          {showEditButton ? (
            <IconButton title="Edit" onClick={() => handleEditJob(job.id)}>
              <EditIcon />
            </IconButton>
          ) : null}

          {showDeleteButton ? (
            <IconButtonDelete title="Remove" onClick={() => handleRemoveJobModalShow(job.id)}>
              <TrashBin />
            </IconButtonDelete>
          ) : null}
        </div>
      </Td>
    </Tr>
  );
};
